/**
 * -----------------------------------------------------------------------------
 * Vehicles Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the vehicles of the organization.
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationVehiclesQuery } from "../../../api/network/adminApiServices";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { FilterType } from "../../../api/types/Admin";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useListingFilters } from "../../../hooks/listingFilter";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import { formattedDateTimeBasedOnTimezone } from "../../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import { getVehicleUUID } from "../../map-fleet/common";
import AdminsVehiclesCreateNew from "./createNewVehicle";

const columnNames = {
  assetId: "Asset ID",
  description: "Description",
  displayname: "Display Name",
  drivername: "Driver",
  driverId: "Driver ID",
  uniqueId: "Unique ID",

  assetVin: "Asset VIN",
  // simPhoneNumber: "SIM Phone Number",
  serverId: "Server ID",
  ignitionState: "Ignition State",
  cameraId: "Camera ID",
  active: "Active",
  lastTimestamp: "Last Timestamp",
};

const columnOrderingFields: Record<string, string> = {
  [columnNames.assetId]: "deviceid",
  [columnNames.description]: "description",
  [columnNames.displayname]: "displayname",
  [columnNames.drivername]: "drivername",
  [columnNames.driverId]: "driverid",
  // [columnNames.uniqueId]: "uniqueid", // intentionally excluded from sortability
  // [columnNames.assetVin]: "vehicleid", // intentionally excluded from sortability
  // [columnNames.serverId]: "devicecode", // intentionally excluded from sortability
  [columnNames.ignitionState]: "ignition_state",
  [columnNames.cameraId]: "cameraid",
  [columnNames.active]: "isactive",
  [columnNames.lastTimestamp]: "lasteventtimestamp",
};

export type TAllColumn = {
  name: string;
  acl: string;
  accountLevelAccess?: boolean;
};

const ScreenDashboardAdminVehicles = () => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.vehicles" });
  const { t: tMain } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { vehicles: orgVehiclesQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  const { getUserACLAccessLevel } = useACL();

  // video allowed at account level
  const thisOrgViewVideoAllowed = useLoggedInUserData("viewVideoAllowed");
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const [all_columns, setAllColumns] = React.useState<TAllColumn[]>([]);
  useEffect(() => {
    setAllColumns([
      { name: columnNames.assetId, acl: "" }, // "Asset ID"
      { name: columnNames.description, acl: "" }, // "Description"
      { name: columnNames.displayname, acl: "" },
      ...(showDriver ? [{ name: columnNames.drivername, acl: "" }] : []),
      { name: columnNames.driverId, acl: "" }, // "Driver ID"
      { name: columnNames.uniqueId, acl: ACL_NAMES.ACL_ASSET_UNIQUE_ID }, // "Unique ID"

      { name: columnNames.assetVin, acl: "" }, // "Asset VIN"
      { name: columnNames.serverId, acl: "" }, // "Server ID" // , acl: ACL_NAMES.ACL_ASSET_SERVER_ID
      { name: columnNames.ignitionState, acl: "" }, // "Ignition State"
      {
        name: columnNames.cameraId,
        acl: ACL_NAMES.ACL_ASSET_EDIT_CAMERA,
        accountLevelAccess: thisOrgViewVideoAllowed === "1",
      }, // "Camera ID"
      { name: columnNames.active, acl: ACL_NAMES.ACL_ASSET_ACTIVE }, // "Active"
      { name: columnNames.lastTimestamp, acl: "" }, // "Last Timestamp"
    ]);
  }, [thisOrgViewVideoAllowed]);

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.Vehicles);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns
      .filter((column) => {
        return (
          (column.acl
            ? getUserACLAccessLevel(column.acl) > ROLE_ACCESS_LEVELS.NONE
            : true) &&
          (column?.accountLevelAccess !== undefined
            ? column?.accountLevelAccess
            : true)
        );
      })
      .map((colItem, _) => {
        return {
          name: colItem.name,
          show: !!savedColumns ? savedColumns.includes(colItem.name) : true,
        };
      })
  );
  useEffect(() => {
    setColumns(
      all_columns
        .filter((column) => {
          return (
            (column.acl
              ? getUserACLAccessLevel(column.acl) > ROLE_ACCESS_LEVELS.NONE
              : true) &&
            (column?.accountLevelAccess !== undefined
              ? column?.accountLevelAccess
              : true)
          );
        })
        .map((colItem, _) => {
          return {
            name: colItem.name,
            show: !!savedColumns ? savedColumns.includes(colItem.name) : true,
          };
        })
    );
  }, [all_columns]);

  // filter mechainism
  const { filters } = useListingFilters();
  const [activeFilterSlug, setActiveFilterSlug] = React.useState<FilterType>(
    filters?.[0]?.slug
  );

  // updating query params on filter change
  useEffect(() => {
    const newParams = {
      ...listingQueryParams,
      vehicles: {
        ...orgVehiclesQueryParams,
        isactive: undefined,
        page: 1,
        ...(activeFilterSlug === "both"
          ? {}
          : {
              isactive: activeFilterSlug === "active" ? 1 : 0,
            }),
      },
    };
    dispatch(setListingQueryParams(newParams));
  }, [activeFilterSlug]);

  // preparing query params

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        vehicles: { ...orgVehiclesQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        vehicles: {
          ...orgVehiclesQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  const {
    data: dataOrgVehicles,
    isFetching: isFetchingOrgVehicles,
    error,
  } = useOrganizationVehiclesQuery({
    ...orgVehiclesQueryParams,
    // 'search_fields' intentionally added here
    // the dynamic search fields (configurable in account admin) defined in the API service are used for asset dropdowns,
    // however, here on the asset listing page the default set of search fields defined in APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE_LISTING need to be used
    search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE_LISTING.join(","),
  });
  const { count, next, previous, results } = dataOrgVehicles || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgVehicles]);

  // retrieve account timezone
  const { tzForDateFns } = useAccountTimezone();

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TLegacyVehicle[])).map(
        (item: TLegacyVehicle, index: number) => ({
          tableRowId: `asset-${item.accountid}-${item.deviceid}`,
          navLink: `${
            routeUrls.dashboardChildren.adminChildren.vehicles
          }/${getVehicleUUID(item)}`,
          cellData: columns
            .map((col, _) => {
              switch (col.name) {
                case columnNames.assetId: // "Asset ID"
                  return item?.deviceid;
                case columnNames.description: // "Description"
                  return item?.description;
                case columnNames.displayname: // "Display Name"
                  return item?.displayname;
                case columnNames.drivername:
                  return showDriver ? (item?.drivername ?? "-") : "-";
                case columnNames.driverId: // "Driver ID"
                  return item?.driverid;
                case columnNames.uniqueId: // "Unique ID"
                  return item?.uniqueid ?? "-";

                case columnNames.assetVin: // "Asset VIN"
                  return item?.vehicleid ?? "-"; //item?.equipment_type
                // case "SIM Phone Number":
                //   return "-";
                case columnNames.serverId: // "Server ID"
                  return item?.devicecode ?? "-";
                case columnNames.ignitionState: // "Ignition State"
                  return item?.ignition_state ?? "-";
                case columnNames.cameraId: // "Camera ID"
                  return item?.cameraid ?? "-"; // TODO: Need exact field from client
                case columnNames.active: // "Active"
                  return item?.isactive === 1 ? (
                    <span className="text-field-success">{tMain("yes")}</span>
                  ) : item?.isactive === 0 ? (
                    <span className="text-field-error-dark">{tMain("no")}</span>
                  ) : (
                    "-"
                  );
                case columnNames.lastTimestamp: // "Last Timestamp"
                  return !!item?.lasteventtimestamp && !!tzForDateFns
                    ? formattedDateTimeBasedOnTimezone(
                        item?.lasteventtimestamp * 1000,
                        tzForDateFns
                      )
                    : "-";
                default:
                  return "-";
              }
            })
            .filter((_, index) => columns[index]?.show),
          // logic below replaced by switch case above to ensure correct column order
          // [
          //   item?.deviceid, // "Asset ID",
          //   item?.driverid, // "Driver ID",
          //   item?.uniqueid ?? "-", // "Unique ID",
          //   item?.description, // "Description",
          //   item?.vehicleid ?? "-", // "Asset VIN" / "Equipment Type (VIN)", //item?.equipment_type
          //   // "-", // "SIM Phone Number",
          //   item?.devicecode ?? "-", // "Server ID",
          //   item?.ignition_state ?? "-", // "Ignition State",
          //   item?.cameraid ?? "-", // "Camera ID", // TODO: Need exact field from client
          //   item?.isactive === 1 ? (
          //     <span className="text-field-success">{tMain("yes")}</span>
          //   ) : item?.isactive === 0 ? (
          //     <span className="text-field-error-dark">{tMain("no")}</span>
          //   ) : (
          //     "-"
          //   ), // "Active"
          //   !!item?.lastupdatetime && !!tzForDateFns
          //     ? formattedDateTimeBasedOnTimezone(
          //         item?.lastupdatetime * 1000,
          //         tzForDateFns
          //       )
          //     : "-", // "Last Timestamp"
          // ].filter((_, index) => columns[index]?.show),
          isActive: item?.isactive === 1,
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <div className="flex h-48 overflow-auto">
          <pre className="text-xs">
            all_columns
            <br />
            {JSON.stringify(all_columns, null, 2)}
          </pre>
          <pre className="text-xs">
            columns
            <br />
            {JSON.stringify(columns, null, 2)}
          </pre>
        </div>
      )}
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ASSET_DEVICE) >=
            ROLE_ACCESS_LEVELS.ALL && {
            buttonText: t("add_new"),
            buttonCallback: () => {
              dispatch(
                setModalsData({ ...modalsState, showCreateVehicle: true })
              );
            },
          })}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgVehiclesQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgVehiclesQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgVehiclesQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
          filters={filters}
          activeFilterSlug={activeFilterSlug}
          handleFilterChange={(slug) => setActiveFilterSlug(slug)}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Vehicles}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgVehicles} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgVehicles}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgVehiclesQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          <Pagination
            pageSize={orgVehiclesQueryParams.page_size}
            handlePageSizeChange={(e: TSelectboxOption | null) => {
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  vehicles: {
                    ...orgVehiclesQueryParams,
                    page: 1,
                    page_size: parseInt(`${e?.value}`),
                  },
                })
              );
            }}
            totalPages={
              count ? Math.ceil(count / orgVehiclesQueryParams.page_size) : 1
            }
            forcePage={orgVehiclesQueryParams.page - 1}
            handlePageClick={(data: TPaginationSelected) => {
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  vehicles: {
                    ...orgVehiclesQueryParams,
                    page: data?.selected + 1,
                  },
                })
              );
            }}
            paginCurrentPage={orgVehiclesQueryParams.page}
            paginItemsOnCurrentPage={results?.length || 0}
            paginTotalItemsCount={count}
            isLoading={isFetchingOrgVehicles}
          />
        </div>
      </div>
      <AdminsVehiclesCreateNew />
    </>
  );
};

export default ScreenDashboardAdminVehicles;
